import React from "react";
import SEO from "../components/SEO";
import enterpriseScopeImg from "../images/enterprise-scope.svg";
import { DEMO_LINK } from "../components/demoBooking";

export default function Enterprise() {
  return (
    <main className="page container enterprisePage">
      <SEO
        title="BossDrop | Enterprise"
        description="BossDrop for Enterprise: automate operating cadence across Slack, Teams, email, calendars, and your task tool. Exec briefs, owner-tagged decisions, and escalations with enterprise controls."
        canonical="https://bossdrop.ai/enterprise"
        url="https://bossdrop.ai/enterprise"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "BossDrop",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          url: "https://bossdrop.ai/",
          publisher: {
            "@type": "Organization",
            name: "BossDrop",
            url: "https://bossdrop.ai/",
          },
        }}
        breadcrumbsJsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://bossdrop.ai/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Enterprise",
              item: "https://bossdrop.ai/enterprise",
            },
          ],
        }}
      />

      <section className="hero">
        <h1 className="h1 reveal" data-reveal>
          Enterprise
        </h1>

        <p className="sub reveal" data-reveal style={{ "--d": "80ms" }}>
          BossDrop runs the operating cadence across your tools. It collects
          status, surfaces risks, tags owners, and ships an exec-ready brief.
          Less coordination overhead. Faster decisions.
        </p>

        <div className="kpis reveal" data-reveal style={{ "--d": "140ms" }}>
          <div className="pill">Exec-ready weekly brief</div>
          <div className="pill">Owner-tagged decisions</div>
          <div className="pill">Auto-escalations before things slip</div>
        </div>

        <div className="ctaBand reveal" data-reveal style={{ "--d": "200ms" }}>
          <a className="btn btnPrimary spacing" href="/contact-sales">
            Contact sales
          </a>
          <a className="btn btnPrimary" {...DEMO_LINK}>
            Book a demo
          </a>
        </div>
      </section>

      <figure style={{ margin: "8px auto 22px", maxWidth: 900 }}>
        <div className="figWrap">
          <img
            src={enterpriseScopeImg}
            alt="Enterprise scope: four team pods, each with its own BossDrop AI layer, rolling up into a single org-level executive view, with SSO, advanced permissions, cross-team reporting, and priority support. Many teams, cross-team rollups, enterprise controls."
            loading="lazy"
            decoding="async"
            style={{
              width: "100%",
              height: "auto",
              display: "block",
            }}
          />
        </div>
      </figure>

      {/* What you get (core + enterprise add-ons, still one section) */}
      <section className="panel reveal wyPanel" data-reveal>
        <h2 className="h2">What you get</h2>

        <div className="grid cards">
          <div className="card wyCard">
            <h3>Executive brief</h3>
            <p className="muted">
              One snapshot: on track, at risk, owner, and next decision.
            </p>
          </div>

          <div className="card wyCard">
            <h3>Automatic status collection</h3>
            <p className="muted">
              Pings owners in Teams/Slack and consolidates updates
              automatically.
            </p>
          </div>

          <div className="card wyCard">
            <h3>Risk detection + escalation</h3>
            <p className="muted">
              Finds drift early and escalates with a concrete ask.
            </p>
          </div>

          <div className="card wyCard">
            <h3>Auto-created work</h3>
            <p className="muted">
              Creates tasks in Jira, Asana, Linear, ClickUp, Notion, monday.com
              or Planner with owner, due date, and context.
            </p>
          </div>

          <div className="card wyCard">
            <h3>Meeting automation</h3>
            <p className="muted">
              Schedules only when needed, with agenda and attendees attached.
            </p>
          </div>

          <div className="card wyCard">
            <h3>Cross-team rollups</h3>
            <p className="muted">
              Leadership view across teams without maintaining dashboards.
            </p>
          </div>
        </div>

        <div className="wyDivider" aria-hidden="true">
          <span>Enterprise add-ons</span>
        </div>

        <div className="grid cards">
          <div className="card wyCard wyCardEnterprise">
            <h3>Org-specific automations</h3>
            <p className="muted">
              You request the workflows. We build and maintain them.
            </p>
          </div>

          <div className="card wyCard wyCardEnterprise">
            <h3>Enterprise onboarding</h3>
            <p className="muted">
              Connect tools, map ownership, define KPIs, launch fast.
            </p>
          </div>

          <div className="card wyCard wyCardEnterprise">
            <h3>SLAs & priority support</h3>
            <p className="muted">
              Dedicated channel + rollout support through security/procurement.
            </p>
          </div>
        </div>
      </section>

      <section className="panel panelDark reveal secPanel" data-reveal>
        <h2 className="h2 secH2">
          <span className="h2Icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4Z"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="M9 12l2 2 4-4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </span>
          Security & controls
        </h2>

        <ul className="bullets secList">
          <li>Least-privilege scopes + tenant isolation</li>
          <li>Audit trail for actions taken by BossDrop</li>
          <li>Retention controls (and deletion on request)</li>
          <li>SSO / SCIM (available or roadmap, depending on setup)</li>
          <li>Data not used to train shared models</li>
        </ul>
      </section>

      <section className="panel reveal rolloutPanel" data-reveal>
        <h2 className="h2">Rollout (simple)</h2>
        <div className="grid cards">
          <div className="card">
            <h3>Connect your tools</h3>
            <p className="muted">
              Teams/Slack + task system + calendar. Minimal access and scope.
            </p>
          </div>
          <div className="card">
            <h3>Define cadence & KPIs</h3>
            <p className="muted">
              What gets reported weekly, what gets escalated, and who should be
              pinged.
            </p>
          </div>
          <div className="card">
            <h3>Pilot → expand</h3>
            <p className="muted">
              Prove value on one group, then roll up additional teams and
              leadership views.
            </p>
          </div>
        </div>
      </section>

      <section className="ctaBand reveal" data-reveal>
        <div className="ctaInner ctaBottom">
          <h3 style={{ margin: 0 }}>Ready to automate your busywork?</h3>
          <p className="muted" style={{ margin: "4px 0 10px" }}>
            BossDrop collects updates, escalates risk early, and ships
            exec-ready briefs so you can focus on real leadership: decision
            making, mentoring, and execution.
          </p>
          <a className="btn btnPrimary" href="/contact-sales">
            Contact sales
          </a>
        </div>
      </section>
    </main>
  );
}
